import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Mail, ArrowRight, CheckCircle2, ShieldAlert } from 'lucide-react';
import { theme } from '../styles/theme';
import LogoComponent from '../components/Logo';
import { AuthAPI } from '../services/api';

const Container = styled.div`
  min-height: 100vh;
  background: ${theme.colors.backgroundSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[4]};
`;

const Card = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.lg};
  padding: ${theme.spacing[10]} ${theme.spacing[8]};
  width: 100%;
  max-width: 480px;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${theme.spacing[6]};
  width: 135px;
  height: auto;
  margin-left: auto;
  margin-right: auto;
`;

const TitleBlock = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[6]};
`;

const Title = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[2]} 0;
  font-family: ${theme.typography.fontFamily.display};
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  color: ${theme.colors.textMuted};
  margin: 0;
  font-size: ${theme.typography.fontSize.base};
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[5]};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  text-align: left;
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textPrimary};
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.div`
  position: absolute;
  left: ${theme.spacing[3]};
  color: ${theme.colors.textMuted};
  z-index: 1;
  display: flex;
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[3]} ${theme.spacing[3]} ${theme.spacing[10]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  background: ${theme.colors.surface};
  color: ${theme.colors.textPrimary};
  transition: all ${theme.transitions.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100] || 'rgba(0,0,82,0.12)'};
  }

  &::placeholder { color: ${theme.colors.textMuted}; }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.primary[500]};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) { background: ${theme.colors.primary[600]}; }
  &:disabled { opacity: 0.7; cursor: not-allowed; }
`;

const ErrorBanner = styled.div`
  background: ${theme.colors.error?.[100] || '#fee'};
  color: ${theme.colors.error?.[700] || '#c00'};
  border: 1px solid ${theme.colors.error?.[100] || '#fbb'};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  display: flex;
  gap: ${theme.spacing[3]};
  align-items: flex-start;
  line-height: 1.5;
  margin-top: ${theme.spacing[4]};

  svg { flex-shrink: 0; margin-top: 2px; }
`;

const FooterRow = styled.p`
  margin: ${theme.spacing[6]} 0 0 0;
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};

  a {
    color: ${theme.colors.primary[500]};
    text-decoration: none;
    font-weight: ${theme.typography.fontWeight.medium};
    &:hover { text-decoration: underline; }
  }
`;

const SuccessIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.success?.[100] || '#dcfce7'};
  color: ${theme.colors.success?.[700] || '#15803d'};
  margin-bottom: ${theme.spacing[4]};
`;

const SuccessMessage = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.base};
  line-height: 1.6;
  margin: 0 0 ${theme.spacing[4]} 0;
`;

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const result = await AuthAPI.requestPasswordReset(email.trim().toLowerCase());
    setSubmitting(false);

    if (result.success) {
      setSent(true);
      return;
    }
    setError(result.error || 'Could not send the reset link. Try again in a moment.');
  };

  if (sent) {
    return (
      <Container>
        <Card style={{ textAlign: 'center' }}>
          <LogoContainer>
            <LogoComponent size={50} />
          </LogoContainer>
          <SuccessIcon>
            <CheckCircle2 size={28} />
          </SuccessIcon>
          <Title>Check your inbox.</Title>
          <SuccessMessage>
            If an account exists for <strong>{email}</strong>, we've sent a password
            reset link. The link expires in 30 minutes — request another one if it
            does.
          </SuccessMessage>
          <FooterRow>
            <Link to="/login">&larr; Back to sign in</Link>
          </FooterRow>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <LogoContainer>
          <LogoComponent size={50} />
        </LogoContainer>
        <TitleBlock>
          <Title>Forgot your password?</Title>
          <Subtitle>
            Enter the email on your account and we'll send you a link to choose
            a new password.
          </Subtitle>
        </TitleBlock>

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="email">Email address</Label>
            <InputContainer>
              <InputIcon><Mail size={16} /></InputIcon>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@yourcompany.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </InputContainer>
          </InputGroup>

          <SubmitButton type="submit" disabled={submitting || !email.trim()}>
            {submitting ? 'Sending reset link...' : 'Send reset link'}
            {!submitting && <ArrowRight size={16} />}
          </SubmitButton>
        </Form>

        {error && (
          <ErrorBanner>
            <ShieldAlert size={18} />
            <span>{error}</span>
          </ErrorBanner>
        )}

        <FooterRow>
          Remembered it? <Link to="/login">Sign in</Link>
        </FooterRow>
      </Card>
    </Container>
  );
};

export default ForgotPasswordPage;
